import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from './lib/queryClient';
import { initSentry } from './lib/sentry';
import { initAnalytics } from './lib/analytics';
import { router } from './router';
import './index.css';

// Initialize error/perf monitoring as early as possible. No-op without a DSN.
initSentry();
// Initialize Firebase Analytics (GA4). No-op without Firebase config. Optional,
// so a failed lazy import (e.g. a stale chunk after a deploy) is swallowed
// rather than surfacing as an unhandled rejection.
void initAnalytics().catch(() => {});

// A chunk that will not load is almost always a stale tab: the page was opened
// before a deploy and is asking for a filename that build no longer produces.
// Reloading picks up the current index.html. Once only, so a genuinely broken
// build cannot put the browser in a refresh loop.
const RELOAD_FLAG = 'po:chunk-reloaded';
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem(RELOAD_FLAG)) return;
  event.preventDefault();
  sessionStorage.setItem(RELOAD_FLAG, '1');
  window.location.reload();
});
window.addEventListener('load', () => sessionStorage.removeItem(RELOAD_FLAG));

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
